import { Component, OnInit, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { WebApplicationsService, AppUrl, AppQueryParameter } from '@summit/application-list';
import { WebUser, WebUserService, ProfileChangeHistoryService, ActivityRecord } from '@summit/web-user-info';
import { AccessLevelEnum, Account, ProfileTypeEnum, WebUserCore } from '@summit/utils';

import { BehaviorSubject, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import * as _ from 'lodash';

import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth.service';
import { CanComponentDeactivate } from 'src/app/can-deactivate.guard';

@Component({
  selector: 'profile-manage-user',
  templateUrl: './manage-user.component.html',
  providers: [ProfileChangeHistoryService],
  styleUrls: ['../new-user/new-user.component.scss']
})
export class ManageUserComponent implements OnInit, CanComponentDeactivate {
  public webUser: WebUser;

  public readonly environment = environment;
  public readonly accessLevelEnum = AccessLevelEnum;
  public isUserValid: boolean;
  public profileType: string;
  public isAdding: boolean;
  public isLoading = true;
  public isSubmitting = false;
  public errorMessage: string;
  public saveDisabledText: string;
  public hasProducerAccount: boolean;
  public paramValues: AppQueryParameter[] = [{parameter: 'switchAccount', value: 'true'}];

  private updateActivity$ = new BehaviorSubject<boolean>(false);
  private unsubscribe$ = new Subject<boolean>();

  constructor(
    public profileChangeHistoryService: ProfileChangeHistoryService,
    public authService: AuthService,
    private webUserService: WebUserService,
    private location: Location,
    private router: Router,
    private webAppsService: WebApplicationsService
  ) { }

  @HostListener('window:beforeunload')
  public canDeactivate(): boolean {
    if (this.profileChangeHistoryService.isPendingChanges) {
      return false;
    }
    if (this.webUser) {
      this.webUserService.updateLockedStatus(this.webUser.uuid, false).subscribe();
    }
    this.unsubscribe$.next(true);
    return true;
  }

  public ngOnInit(): void {
    this.webUserService.getUserCore(null, environment.restfulService, environment.isNotLocalhost)
      .subscribe((webUser: WebUserCore) => {
        if (!webUser) {
          this.router.navigate(['/new']);
        } else {
          this.webUser = webUser as WebUser;
          this.profileChangeHistoryService.pristineWebUser = _.cloneDeep(this.webUser);
          this.webUserService.updateLockedStatus(this.webUser.uuid, true).subscribe();
        }
        this.isLoading = false;
      });
  }

  public webUserInfoChanged() {
    this.profileChangeHistoryService.checkIfWebUserInfoUpdated(false, this.webUser, this.authService.getUserName());
    this.profileChangeHistoryService.checkIfRestrictedApplicationListUpdated(false, this.webUser, this.authService.getUserName());
  }

  public save(): void {
    this.isSubmitting = true;
    const coreUser: WebUser = _.cloneDeep(this.webUser);
    let activityHistory = new Array<ActivityRecord>();

    // make sure the note does not already exist
    activityHistory.push(...this.profileChangeHistoryService.getUniqueRecords(this.webUser, this.authService.getUserName()));
    activityHistory = _.uniq(activityHistory);

    this.webUserService.updateUserCore(coreUser as WebUserCore, environment.restfulService, environment.isNotLocalhost)
      .pipe(
        tap(() => {
          this.profileChangeHistoryService.saveNotesToImageRight(environment.restfulService, environment.isNotLocalhost);
          this.updateActivityHistory(activityHistory, 0);
        })
      ).subscribe(
        () => {},
        err => {
          this.errorMessage = err;
        }
      );

    this.updateActivity$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isComplete: boolean) => {
        if (isComplete) {
          this.profileChangeHistoryService.isPendingChanges = false;
          // if agency user, check to see if any emails need to be sent to Sales/agency admins
          if (this.webUser.profileType === ProfileTypeEnum.AGENT) {
            const pendingAccounts: Account[] = _.differenceWith(this.webUser.pendingAccounts,
                                                this.profileChangeHistoryService.pristineWebUser.pendingAccounts, _.isEqual);
            const inactiveAccounts: Account[] = _.differenceWith(this.webUser.inactiveAccounts,
                                                  this.profileChangeHistoryService.pristineWebUser.inactiveAccounts, _.isEqual);

            // send an email to the agency admin(s) notifying them that a user added a new pending account
            if (pendingAccounts.length > 0) {
              // only send the new pending accounts in the request to prevent duplicate emails being sent for older pending accounts
              coreUser.pendingAccounts = pendingAccounts;
              this.webUserService.sendAgencyAdminEmails(coreUser, environment.restfulService, environment.isNotLocalhost)
              .subscribe(() => {
                if (inactiveAccounts.length > 0) {
                  this.sendInactivatedAgencyEmail(inactiveAccounts);
                } else {
                  this.goToBusinessCenter();
                }
              });
            } else if (inactiveAccounts.length > 0) {
              this.sendInactivatedAgencyEmail(inactiveAccounts);
            } else {
              this.goToBusinessCenter();
            }
          } else {
            this.goToBusinessCenter();
        }
      }
    });
  }

  public goToBusinessCenter(): void {
    this.webAppsService.getAppUrl('business_center', this.paramValues, environment.restfulService, true)
        .subscribe((appUrl: AppUrl) => window.location.href = appUrl.url);
  }

  public goBack(): void {
    this.location.back();
  }

  public updateProfileType(profileType: string): void {
    this.profileType = profileType;

    switch (profileType) {
      case ProfileTypeEnum.AGENT:
        this.saveDisabledText = 'an agency';
        break;
      case ProfileTypeEnum.INTERNAL:
        this.saveDisabledText = 'a department';
        break;
      case ProfileTypeEnum.PAYROLLCOMPANY:
        this.saveDisabledText = 'a company';
        break;
      case ProfileTypeEnum.INSURED:
        this.saveDisabledText = 'a policy';
        break;
      default: this.saveDisabledText = 'an account';
    }
  }

  private sendInactivatedAgencyEmail(inactiveAccounts: Account[]): void {
    // if any new accounts have been inactivated, send appropriate emails for each newly inactivated account...
    for (let i = 0; i < inactiveAccounts.length; i++ ) {
      const account = inactiveAccounts[i];
      // send an email to the agency admin(s) notifying them that a user inactivated their own account
      this.webUserService.sendInactivatedAgencyEmail(this.webUser.emailAddress, account, false).subscribe(
        () => {
          if (account.role.toLowerCase() === 'producer') {
            // if account is a producer type, send inactivated email to Sales group
            this.webUserService.sendInactivatedAgencyEmail(this.webUser.emailAddress, account, true).subscribe(
              () => {},
              err => {
                console.error(err);
              }
            );
          }
          if (i === inactiveAccounts.length - 1) {
            this.goToBusinessCenter();
          }
        },
        err => {
          console.error(err);
        }
      );
    }
  }

  private updateActivityHistory(activityHistory: ActivityRecord[], currentIndex: number) {
    if (activityHistory.length <= 0) {
      this.updateActivity$.next(true);
    } else {
      this.webUserService.addActivityRecord(this.webUser.uuid, activityHistory[currentIndex].note)
        .subscribe(() => {
          currentIndex++;
          if (currentIndex < activityHistory.length) {
            this.updateActivityHistory(activityHistory, currentIndex);
          } else {
            this.updateActivity$.next(true);
          }
        });
    }
  }
}
